'use client';

import { useState, useEffect } from 'react';
import { useUiContent } from '@/components/ui-content-provider';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { Category } from 'shared/src/types/categories.type';
import { checkIsAdminAction } from '@/server/sitemap/check-admin-action';
import { getAiDisclosureLine } from '@/lib/ui-content/app-compliance-strings';
import { ReportContentButton } from '@/components/report/report-content-button';
import { FooterColumns } from '@/components/footer-columns';

type Props = {
  lang: LocaleDetails;
  style?: any;
  data?: Category;
  // Static chrome (region picker, legal, careers, search landing) has no
  // generated content to disclose or report. Generated pages still carry both
  // (App Store 1.2 / Play UGC).
  hideAiDisclosure?: boolean;
  hideReport?: boolean;
  // Brand mark above the Region column. Opt-in per page — see FooterLogo.
  showMark?: boolean;
  // Pass false where the page already is home (the search landing).
  markLinksHome?: boolean;
};

/**
 * Client-side Footer component for use in streaming/client contexts.
 * For server-side pages, use the async Footer from '@/components/footer' instead.
 */
export function FooterClient({
  lang,
  style,
  data,
  hideAiDisclosure,
  hideReport,
  showMark,
  markLinksHome,
}: Props) {
  const ui = useUiContent();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const admin = await checkIsAdminAction();
        setIsAdmin(admin);
      } catch (error) {
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, []);

  return (
    <footer className="text-sm leading-6 z-50 backdrop-blur-sm" style={style}>
      <div className="border-t border-hairline shadow-top-glow">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-8 lg:px-8">
          <FooterColumns
            lang={lang}
            showMark={showMark}
            markLinksHome={markLinksHome}
            labels={{
              articles: ui?.articles,
              vision: ui?.vision,
              methodology: ui?.methodology,
              contactUs: ui?.contactUs,
              careers: ui?.careers,
              partners: ui?.partners,
              trademarkPolicy: ui?.trademarkPolicy,
              privacyPolicy: ui?.privacyPolicy,
              changeRegion: ui?.changeRegion,
            }}
          />

          {/* Below-links bar: AI disclosure, then copyright (Play AI-content / Apple 1.2) */}
          <div className="mt-12 flex flex-col items-center gap-6 border-t border-hairline pt-8">
            {(!hideAiDisclosure || !hideReport) && (
              <div className="flex flex-col items-center gap-4 text-center text-sm text-muted">
                {!hideAiDisclosure && <p>{getAiDisclosureLine(lang.languageCode)}</p>}
                {!hideReport && <ReportContentButton languageCode={lang.languageCode} />}
              </div>
            )}

            <div className="flex flex-col flex-wrap items-center justify-center gap-3 text-muted sm:flex-row sm:gap-x-8 sm:gap-y-1">
              <p className="shrink-0">
                {`Copyright © 2023-${new Date().getFullYear()} InceptionAI Inc.`}
              </p>
              <p className="text-sm">
                {ui?.slogan}
              </p>
            </div>
          </div>

          {/* Admin door. Quiet on purpose: it is an operator control sitting on a
              visitor-facing page, so it reads as a link, not a promotion. */}
          {isAdmin && (
            <div className="mt-8 flex justify-center">
              <a
                href="/admin"
                className="group inline-flex items-center gap-2.5 rounded-full border border-hairline px-6 py-3 text-sm font-medium text-muted transition-colors duration-200 hover:border-accent hover:text-accent"
              >
                <svg
                  className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Admin</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
